import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { SendIcon, RefreshIcon } from "../Auth/onboarding/icons";
import { suggestedPrompts } from "./botReplies";
import { readWidgetTheme } from "../../utils/widgetTheme";
import WidgetGlyph from "../../Components/WidgetGlyph";
import { getWidgetState } from "../../actions/assistants";
import {
  connectVisitor,
  sendVisitorMessage,
  getOrCreateVisitorId,
  resetVisitorId,
} from "../../utils/visitorSocket";

function now() {
  return new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

const DEFAULT_NAME = "Maya";

// Server messages use { role, content }; the thread renders { sender, text }.
const toMessage = (message) => ({
  sender: message.role === "user" ? "user" : "assistant",
  text: message.content,
  time: "",
  earlier: true,
});

// The config's welcomeMessage wins — it's what the live widget opens with —
// falling back to the assistant record and then to a generated greeting.
const openingMessage = (assistant, config) => ({
  sender: "assistant",
  text:
    config?.welcomeMessage ||
    assistant?.welcomeMessage ||
    `Hi! I'm ${config?.name || assistant?.name || DEFAULT_NAME} 👋 How can I help you today?`,
  time: now(),
});

// The theme is turned into scoped custom properties so the existing rules can
// pick it up without a second set of themed classes. Every value falls back to
// the app default, so a partial or missing theme still renders correctly.
const themeVars = (config) => {
  const theme = readWidgetTheme(config);
  const vars = {};

  if (theme.primaryColor) vars["--asst-primary"] = theme.primaryColor;
  if (theme.headerColor) vars["--asst-header"] = theme.headerColor;
  if (theme.accentColor) vars["--asst-accent"] = theme.accentColor;
  if (theme.buttonColor) vars["--asst-button"] = theme.buttonColor;
  if (theme.fontFamily) vars["--asst-font"] = theme.fontFamily;
  if (theme.fontSize) vars["--asst-font-size"] = `${theme.fontSize}px`;

  // "square" and "rounded" are the widget's two bubble shapes; anything else
  // defers to the numeric radius.
  if (theme.bubbleStyle === "square") {
    vars["--asst-bubble-radius"] = "4px";
  } else if (typeof theme.borderRadius === "number") {
    vars["--asst-bubble-radius"] = `${theme.borderRadius}px`;
  }

  return vars;
};

// `config` is the GET /assistants/:id/config payload, fetched once by the page
// and passed down rather than re-requested here.
const ChatTester = forwardRef(function ChatTester({ assistant, config }, ref) {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState(() => [
    openingMessage(assistant, config),
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState("");
  // Set when the account or this assistant is switched off, or the plan's
  // conversations for the month are gone: there is nothing to connect to.
  const [blocked, setBlocked] = useState("");
  const threadRef = useRef(null);
  const socketRef = useRef(null);
  const conversationRef = useRef(null);

  const assistantId = assistant?.id;

  // The config arrives after the first render, so the greeting is refreshed
  // once it lands — but only while the thread is untouched, so a conversation
  // in progress is never rewritten under the user.
  useEffect(() => {
    if (!config) return;

    setMessages((prev) =>
      prev.length === 1 ? [openingMessage(assistant, config)] : prev,
    );
  }, [assistant, config]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, typing]);

  // Talks to the real assistant over the widget's own socket, exactly as a
  // visitor on the customer's site would - so what is tested here is what
  // visitors actually get.
  const openConnection = useCallback(
    (visitorId, options = {}) => {
      socketRef.current = connectVisitor(
        assistantId,
        { visitorId, pageUrl: window.location.href },
        {
          onStarted: (data) => {
            conversationRef.current = data.conversationId || null;
            setConnected(true);
            setError('');

            // Only adopt the socket's history when widget-state didn't
            // already supply it, so the thread isn't rendered twice.
            if (!options.hasHistory && data.messages?.length) {
              setMessages(data.messages.map(toMessage));
            }
          },

          onCreated: (data) => {
            conversationRef.current = data.conversationId || null;
          },

          // Progress events aren't worth showing a visitor; they only mean a
          // reply is still on its way.
          onStage: () => setTyping(true),

          onFinal: (data) => {
            setTyping(false);
            setMessages((prev) => [
              ...prev,
              { sender: 'assistant', text: data.reply, time: now() },
            ]);
          },

          onError: (message, fatal, code) => {
            setTyping(false);

            // Switched off mid-conversation: the socket will refuse
            // everything from here, so the thread closes with the reason
            // instead of collecting errors.
            if (code === "assistant_paused") {
              setBlocked(message);
              socketRef.current?.disconnect();
              socketRef.current = null;
              setConnected(false);
              return;
            }

            setError(message);
          },

          onDisconnect: () => {
            setTyping(false);
            setConnected(false);
          },
        },
      );
    },
    [assistantId],
  );

  useEffect(() => {
    if (!assistantId) return undefined;

    let cancelled = false;
    const visitorId = getOrCreateVisitorId(assistantId);

    const open = async () => {
      // A returning visitor already has a conversation; restoring it first
      // means the thread isn't blank on every reload.
      const state = await dispatch(getWidgetState(assistantId, visitorId));
      if (cancelled) return;

      // The same flags the live widget reads. There is no point opening a
      // socket that will be refused, and the server's sentence says more than
      // a failed connection would.
      if (state?.paused) {
        setBlocked(state.paused.message || "This assistant isn't available right now.");
        return;
      }

      if (state?.quota?.exceeded) {
        setBlocked(
          "This assistant has used all the conversations its plan allows this month.",
        );
        return;
      }

      setBlocked("");

      const history = state?.conversation?.messages || [];

      if (history.length) {
        conversationRef.current = state.conversation.conversationId || null;
        setMessages(history.map(toMessage));
      }

      openConnection(visitorId, { hasHistory: history.length > 0 });
    };

    open();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
      conversationRef.current = null;
      setConnected(false);
    };
  }, [assistantId, dispatch, openConnection]);

  const sendMessage = (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing || !connected) return;

    setMessages((prev) => [...prev, { sender: 'user', text: trimmed, time: now() }]);
    setInput('');
    setTyping(true);
    setError('');

    sendVisitorMessage(
      socketRef.current,
      conversationRef.current,
      trimmed,
      window.location.href,
    );
  };

  // Reset forgets the visitor too, so the next connection starts a genuinely
  // new conversation rather than resuming the one just cleared.
  const handleReset = () => {
    if (!assistantId) return;

    socketRef.current?.disconnect();
    socketRef.current = null;
    conversationRef.current = null;

    resetVisitorId(assistantId);

    setTyping(false);
    setInput('');
    setError('');
    setMessages([openingMessage(assistant, config)]);

    openConnection(getOrCreateVisitorId(assistantId));
  };

  useImperativeHandle(ref, () => ({
    sendMessage,
  }));

  const showSuggestions = messages.length === 1 && !typing;

  // The config's own prompts when it has any, otherwise the built-in set.
  const prompts = config?.suggestedPrompts?.length
    ? config.suggestedPrompts
    : suggestedPrompts;

  // The launcher the assistant is configured with - an uploaded avatar, the
  // icon picked in Customization, or the robot as a last resort. Reading only
  // avatarUrl meant a chosen icon never showed here.
  const theme = readWidgetTheme(config);
  const avatar = <WidgetGlyph theme={theme} size={18} />;
  const displayName = config?.name || assistant?.name || DEFAULT_NAME;

  return (
    <section
      className="dash-card asst-tester-card"
      style={themeVars(config)}
    >
      <div className="asst-tester-header">
        <span className="asst-tester-avatar">
          {avatar}
        </span>
        <div className="asst-tester-heading">
          <strong>{displayName}</strong>
          <span className={`asst-tester-status`}>
            <span className="asst-tester-status-dot" />
            {connected
              ? config?.tagline || "Online • Test mode"
              : "Connecting…"}
          </span>
        </div>
        <button type="button" className="asst-reset-btn" onClick={handleReset}>
          <RefreshIcon />
          Reset conversation
        </button>
      </div>

      <div className="asst-thread" ref={threadRef}>
        {blocked && (
          <p className="asst-thread-blocked" role="status">
            {blocked}
          </p>
        )}

        {error && <p className="auth-error asst-thread-error">{error}</p>}

        {messages.map((message, i) => (
          <div className={`asst-message asst-message--${message.sender}`} key={i}>
            {message.sender === "assistant" && (
              <span className="asst-message-avatar">
                {avatar}
              </span>
            )}
            <div className="asst-message-col">
              <p className="asst-message-bubble">{message.text}</p>
              <span className="asst-message-time">{message.time}</span>
            </div>
          </div>
        ))}

        {typing && (
          <div className="asst-message asst-message--assistant">
            {/* Same avatar as the assistant's messages above — this was
                hardcoded to the robot icon, so the configured one vanished
                while a reply was being composed. */}
            <span className="asst-message-avatar">
              {avatar}
            </span>
            <div className="asst-message-col">
              <p className="asst-message-bubble asst-typing-bubble">
                <span />
                <span />
                <span />
              </p>
            </div>
          </div>
        )}

        {showSuggestions && (
          <div className="asst-suggestions">
            {prompts.map((prompt) => (
              <button type="button" key={prompt} onClick={() => sendMessage(prompt)}>
                {prompt}
              </button>
            ))}
          </div>
        )}
      </div>

      <form
        className="asst-input-row"
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage(input);
        }}
      >
        <input
          type="text"
          placeholder={
            connected
              ? "Type a message to test your assistant..."
              : "Connecting to your assistant..."
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={typing || !connected}
        />
        <button
          type="submit"
          className="asst-send-btn"
          disabled={!input.trim() || typing || !connected}
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </form>
    </section>
  );
});

export default ChatTester;
